/** Reference lists, services catalog and processing years — used across many screens. */
import { baseApi } from './baseApi';
import type { ApiMessage } from './response';
import type { Reference, Service, StateOption, Years } from './types';

const catalogApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    reference: build.query<Reference, undefined>({
      query: () => '/lookups/reference',
      keepUnusedDataFor: 3600, // fixed lists
    }),
    /** A country's states / provinces, for a foreign address. */
    countryStates: build.query<StateOption[], string>({
      query: (code) => `/lookups/countries/${code}/states`,
      keepUnusedDataFor: 3600,
    }),
    services: build.query<Service[], undefined>({
      query: () => '/services',
      providesTags: ['Service'],
    }),
    createService: build.mutation<ApiMessage<Service>, { name: string }>({
      query: (body) => ({ url: '/services', method: 'POST', body }),
      invalidatesTags: ['Service', 'DueRules', 'Audit'],
    }),
    renameService: build.mutation<ApiMessage<Service>, { id: string; name: string }>({
      query: ({ id, name }) => ({ url: `/services/${id}`, method: 'PATCH', body: { name } }),
      invalidatesTags: ['Service', 'DueRules', 'Audit'],
    }),
    years: build.query<Years, undefined>({
      query: () => '/years',
      providesTags: ['Year'],
    }),
    openYear: build.mutation<ApiMessage<Years>, { year: number }>({
      query: (body) => ({ url: '/years', method: 'POST', body }),
      invalidatesTags: ['Year', 'Audit'],
    }),
  }),
});

export const {
  useReferenceQuery,
  useCountryStatesQuery,
  useServicesQuery,
  useCreateServiceMutation,
  useRenameServiceMutation,
  useYearsQuery,
  useOpenYearMutation,
} = catalogApi;
